/**
 * Dashboard Grid Component
 * 
 * Displays available dashboards in a responsive CSS grid layout.
 * Props-based component that receives data from parent.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Skeleton,
  Chip,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { type Dashboard } from '../services/api';
import outputs from '../../amplify_outputs.json';

// API base URL from Amplify outputs (matches v3 pattern)
const API_BASE_URL = (outputs.custom?.apiEndpoint || '').replace(/\/+$/, '');

// Thumbnail component with loading and error states
function DashboardThumbnail({ thumbnailUrl, title, cacheKey }: { thumbnailUrl: string | null; title: string; cacheKey: string }) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  // If no thumbnail URL or error occurred (and exhausted retries), show placeholder
  if (!thumbnailUrl || (hasError && retryCount >= maxRetries)) {
    return (
      <Box
        sx={{
          height: 180,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          color: 'text.secondary',
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 3V21H21" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 14L11 10L15 14L21 8" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.disabled' }}>
          Dashboard Preview
        </Typography>
      </Box>
    );
  }

  // Build full URL by prepending API_BASE_URL to the thumbnailUrl path (matches v3 pattern)
  // Use stable cache key to avoid re-fetching on re-renders, with retry suffix for retry attempts
  const fullUrl = `${API_BASE_URL}${thumbnailUrl}?t=${cacheKey}${retryCount > 0 ? `&retry=${retryCount}` : ''}`;

  return (
    <Box sx={{ height: 180, position: 'relative', bgcolor: 'grey.100' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          }}
        >
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        </Box>
      )}
      <CardMedia
        component="img"
        height="180"
        image={fullUrl}
        alt={title}
        crossOrigin="anonymous"
        sx={{
          objectFit: 'cover',
          objectPosition: 'top center',
          bgcolor: 'white',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease',
        }}
        onLoad={() => {
          setIsLoading(false);
          setHasError(false);
        }}
        onError={() => {
          console.error('[Thumbnail] Failed to load:', fullUrl, 'attempt:', retryCount + 1);
          if (retryCount < maxRetries) {
            // Retry with exponential backoff
            const delay = Math.pow(2, retryCount) * 500; // 500ms, 1000ms
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              setIsLoading(true);
            }, delay);
          } else {
            setIsLoading(false);
            setHasError(true);
          }
        }}
      />
    </Box>
  );
}

interface DashboardGridProps {
  dashboards: Dashboard[];
  isLoading: boolean;
  onSelect: (dashboard: Dashboard) => void;
  externalLink?: boolean;
}

export function DashboardGrid({ dashboards, isLoading, onSelect, externalLink = false }: DashboardGridProps) {
  // Generate stable cache key once when dashboards load - prevents re-fetching on re-renders
  const cacheKey = useMemo(() => String(Date.now()), [dashboards]);

  // Ensure dashboards is always an array to prevent .map errors during navigation
  const safeDashboards = Array.isArray(dashboards) ? dashboards : [];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 3,
        }}
      >
        {[...Array(6)].map((_, index) => (
          <Card key={index} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Skeleton variant="rectangular" height={180} animation="wave" />
            <CardContent>
              <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={16} />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (safeDashboards.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          px: 2,
          maxWidth: 500,
          mx: 'auto',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 1.5,
          }}
        >
          No Dashboards Available
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.625,
          }}
        >
          There are no dashboards configured for your account. Contact your administrator for access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: 3,
      }}
    >
      {safeDashboards.map((dashboard) => {
        // Build metadata text
        const viewText = dashboard.viewCount
          ? `${dashboard.viewCount} View${dashboard.viewCount !== 1 ? 's' : ''}`
          : '0 Views';
        const favText = dashboard.favoriteCount
          ? `${dashboard.favoriteCount} Favorite${dashboard.favoriteCount !== 1 ? 's' : ''}`
          : '0 Favorites';
        const creatorText = dashboard.creatorName ? `Created by ${dashboard.creatorName}` : '';
        const metaParts = [viewText, favText, creatorText].filter(Boolean);
        const metaText = metaParts.join(', ');

        return (
          <Card
            key={dashboard.id}
            onClick={() => onSelect(dashboard)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                borderColor: 'grey.300',
              },
            }}
          >
            <DashboardThumbnail 
              thumbnailUrl={dashboard.thumbnailUrl}  
              title={dashboard.title}
              cacheKey={cacheKey}
            />
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 0.5,
                    color: 'text.primary',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    flex: 1,
                  }}
                >
                  {dashboard.title}
                </Typography>
                {externalLink && (
                  <Chip
                    icon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                    label="Looker"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontSize: '0.65rem',
                      flexShrink: 0,
                      mt: 0.25,
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  lineHeight: 1.5,
                  fontSize: '0.75rem',
                }}
              >
                {metaText}
              </Typography>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

export default DashboardGrid;
